import { t } from "@lingui/macro";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import type { AtsScoreResponse } from "@reactive-resume/dto";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

import { RateLimitWaitlistForm } from "../_components/rate-limit-waitlist-form";
import { ToolShareButtons } from "../_components/tool-share-buttons";
import { buildPublicToolUrl, getApiErrorMessage, recordToolCtaClick } from "../_utils";

type AtsFormState = {
  resume: string;
  jobDescription: string;
};

type AtsFieldErrors = Partial<Record<keyof AtsFormState, string>>;

class ToolApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    // eslint-disable-next-line lingui/no-unlocalized-strings -- Error.name, never shown
    this.name = "ToolApiError";
  }
}

const toolPath = "/tools/ats-checker";

const keywordPillClassName = {
  matched: cn("border-success/30 bg-success-soft text-success-strong"),
  missing: cn("border-warning/30 bg-warning-soft text-warning-strong"),
} as const;

const getScoreColorClassName = (matchPercentage: number) => {
  if (matchPercentage >= 75) {
    return cn("border-success/40 bg-success-soft text-success-strong");
  }

  if (matchPercentage >= 50) {
    return cn("border-warning/40 bg-warning-soft text-warning-strong");
  }

  return cn("border-error/40 bg-error/10 text-error");
};

export const AtsCheckerPage = () => {
  const [form, setForm] = useState<AtsFormState>({ resume: "", jobDescription: "" });
  const [fieldErrors, setFieldErrors] = useState<AtsFieldErrors>({});
  const [result, setResult] = useState<AtsScoreResponse | null>(null);
  const [apiError, setApiError] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");

  const mutation = useMutation<AtsScoreResponse, ToolApiError, AtsFormState>({
    mutationFn: async (payload) => {
      const response = await fetch("/api/tools/ats-score", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = (await response.json().catch(() => ({}))) as AtsScoreResponse | unknown;

      if (!response.ok) {
        throw new ToolApiError(
          response.status,
          getApiErrorMessage(data, t`Something went wrong. Please try again.`),
        );
      }

      return data as AtsScoreResponse;
    },
    onSuccess: (data) => {
      setResult(data);
      setApiError("");
      setRateLimitMessage("");
    },
    onError: (error) => {
      setResult(null);

      if (error.status === 429) {
        setRateLimitMessage(error.message);
        setApiError("");
        return;
      }

      setApiError(error.message);
    },
  });

  const validateForm = () => {
    const nextErrors: AtsFieldErrors = {};

    if (form.resume.trim().length < 50) {
      nextErrors.resume = t`Your resume needs at least 50 characters.`;
    }

    if (form.jobDescription.trim().length < 50) {
      nextErrors.jobDescription = t`The job description needs at least 50 characters.`;
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setApiError("");
    setRateLimitMessage("");

    if (!validateForm()) {
      return;
    }

    mutation.mutate(form);
  };

  const scoreColorClassName = result ? getScoreColorClassName(result.matchPercentage) : "";

  const toolUrl = buildPublicToolUrl(toolPath);
  const matchPercentage = result?.matchPercentage ?? 0;
  const shareMessage = result
    ? t`My resume scored ${matchPercentage}% on the ATS checker. Check yours free: ${toolUrl}`
    : "";

  return (
    <div className="min-h-screen bg-background px-4 pb-24 pt-32 delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both sm:px-6 sm:pt-36">
      <SEO
        title={t`Free ATS Resume Checker | Apply Edge`}
        description={t`Check your resume against any job description with our free ATS scanner. Get an instant match score and keyword recommendations.`}
        canonical="/tools/ats-checker"
      />
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <div className="max-w-3xl space-y-4">
          <Badge className="w-fit border border-sky-400/30 bg-sky-400/10 text-xs font-medium text-sky-700 dark:text-sky-300">
            {t`Free Tool`}
          </Badge>
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
            {t`See how hard your resume would hit an ATS before you apply.`}
          </h1>
          <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
            {t`Paste your resume and the job description. We'll give you a match score, the keywords you've already nailed, the ones you're missing, and the fastest fixes to make before you hit submit.`}
          </p>
        </div>

        {rateLimitMessage ? (
          <Card className="border-amber-400/30 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
            <CardHeader className="space-y-2">
              <CardTitle className="flex items-center gap-2 text-2xl text-foreground">
                <WarningCircleIcon className="text-warning-strong" size={22} />
                {t`Daily limit reached`}
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                {rateLimitMessage}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t`Drop your email here and we'll let you know when unlimited tools are ready.`}
              </p>
              <RateLimitWaitlistForm tool="ATS_CHECKER" />
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <Card className="border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl text-foreground">{t`ATS Match Score Checker`}</CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {t`Minimum 50 characters in each field. Long inputs are safely truncated on the server before the AI run.`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-5" onSubmit={onSubmit}>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground" htmlFor="resume-input">
                      {t`Your Resume`}
                    </label>
                    <textarea
                      id="resume-input"
                      value={form.resume}
                      rows={11}
                      className={cn(
                        "min-h-[220px] w-full rounded-xl border bg-muted px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-sky-400/60 focus:ring-2 focus:ring-sky-400/20",
                        fieldErrors.resume ? "border-rose-400/60" : "border-border",
                      )}
                      placeholder={t`Paste your current resume here...`}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, resume: event.target.value }));
                        setFieldErrors((current) => ({ ...current, resume: undefined }));
                      }}
                    />
                    {fieldErrors.resume ? (
                      <p className="text-sm text-error">{fieldErrors.resume}</p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <label
                      className="text-sm font-medium text-foreground"
                      htmlFor="job-description-input"
                    >
                      {t`Job Description`}
                    </label>
                    <textarea
                      id="job-description-input"
                      value={form.jobDescription}
                      rows={11}
                      className={cn(
                        "min-h-[220px] w-full rounded-xl border bg-muted px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-sky-400/60 focus:ring-2 focus:ring-sky-400/20",
                        fieldErrors.jobDescription ? "border-rose-400/60" : "border-border",
                      )}
                      placeholder={t`Paste the job description you want to target...`}
                      onChange={(event) => {
                        setForm((current) => ({ ...current, jobDescription: event.target.value }));
                        setFieldErrors((current) => ({ ...current, jobDescription: undefined }));
                      }}
                    />
                    {fieldErrors.jobDescription ? (
                      <p className="text-sm text-error">{fieldErrors.jobDescription}</p>
                    ) : null}
                  </div>

                  {apiError ? <p className="text-sm text-error">{apiError}</p> : null}

                  <Button type="submit" className="h-11 w-full" disabled={mutation.isPending}>
                    {mutation.isPending ? (
                      <>
                        <CircleNotchIcon className="mr-2 animate-spin" size={18} />
                        {t`Scoring your resume...`}
                      </>
                    ) : (
                      t`Score my resume`
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {result ? (
              <Card className="border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
                <CardHeader className="gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <CardTitle className="text-2xl text-foreground">{t`Your match score`}</CardTitle>
                    {result.cached ? (
                      <Badge className="border border-success/30 bg-success-soft text-success-strong">
                        {t`Cached result`}
                      </Badge>
                    ) : null}
                  </div>

                  <div
                    className={cn(
                      "flex h-32 w-32 items-center justify-center rounded-full border font-mono text-4xl font-semibold tabular-nums",
                      scoreColorClassName,
                    )}
                  >
                    {result.matchPercentage}%
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs font-medium text-success-strong">
                        <CheckCircleIcon size={16} />
                        {t`Matched keywords`}
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {result.matchedKeywords.length > 0 ? (
                          result.matchedKeywords.map((keyword) => (
                            <span
                              key={keyword}
                              className={cn(
                                "rounded-full border px-3 py-1 text-xs font-medium",
                                keywordPillClassName.matched,
                              )}
                            >
                              {keyword}
                            </span>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">{t`No clear matches found yet.`}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-xs font-medium text-warning-strong">
                        <WarningCircleIcon size={16} />
                        {t`Missing keywords`}
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {result.missingKeywords.length > 0 ? (
                          result.missingKeywords.map((keyword) => (
                            <span
                              key={keyword}
                              className={cn(
                                "rounded-full border px-3 py-1 text-xs font-medium",
                                keywordPillClassName.missing,
                              )}
                            >
                              {keyword}
                            </span>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {t`Nice. You're covering the important keywords already.`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="text-xs font-medium text-muted-foreground">
                      {t`3 fast improvements`}
                    </div>
                    <ol className="space-y-3 text-sm leading-6 text-foreground">
                      {result.suggestions.map((suggestion, index) => (
                        <li
                          key={suggestion}
                          className="flex gap-3 rounded-2xl border border-border bg-muted/40 p-4"
                        >
                          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-xs font-semibold text-sky-700 dark:text-sky-300">
                            {index + 1}
                          </span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <ToolShareButtons
                    message={shareMessage}
                    title={t`Apply Edge ATS Checker`}
                    url={toolUrl}
                  />

                  <Button asChild className="w-full">
                    <Link
                      to="/auth/register"
                      onClick={() => {
                        recordToolCtaClick("ATS_CHECKER", "results");
                      }}
                    >
                      {t`Fix these issues with Apply Edge`}
                      <ArrowRightIcon className="ml-2" size={18} />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
                <CardHeader>
                  <CardTitle className="text-2xl text-foreground">{t`What you'll get`}</CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    {t`A fast pre-application check built for job hunters who want signal, not vague advice.`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm leading-6 text-muted-foreground">
                  <div className="rounded-2xl border border-border bg-muted/40 p-4">
                    {t`Color-coded score so you instantly know if you're close, risky, or way off.`}
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/40 p-4">
                    {t`Matched and missing keyword buckets so you know exactly what the ATS sees.`}
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/40 p-4">
                    {t`Three targeted edits you can make before you apply.`}
                  </div>
                  <div className="rounded-2xl border border-sky-400/20 bg-sky-400/10 p-4 text-sky-700 dark:text-sky-300">
                    {t`Daily limit: 3 free checks. Sign up for more.`}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
